// import * as React from "react";
import { icons } from "lucide-react";
import { forwardRef, useMemo } from "react";
import { cn } from "~/lib/utils";

import { Icon } from "./icon";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onSuffixClick?: () => void;
  onPrefixClick?: () => void;
  suffixClass?: string;
  prefixClass?: string;
  prefixText?: string;
  suffixText?: string;
  prefix?: keyof typeof icons;
  suffix?: keyof typeof icons;
  error?: any;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      suffix,
      onSuffixClick,
      suffixClass,
      prefix,
      onPrefixClick,
      prefixClass,
      prefixText,
      suffixText,
      type,
      ...props
    },
    ref,
  ) => {
    const computedPaddingRight = useMemo(() => {
      if (suffixText) {
        return `${suffixText.length * 6}px`;
      }
      // If only a suffix icon exists, fall back to a default value.
      return suffix ? undefined : undefined;
    }, [suffixText, suffix]);

    return (
      <div className="relative w-full">
        <div className="absolute inset-y-0 left-0 flex cursor-pointer items-center pl-2">
          {prefixText && (
            <div className="h-6 w-6">
              <span className="text-xs">{prefixText}</span>
            </div>
          )}
          {prefix && (
            <Icon
              name={prefix}
              onClick={onPrefixClick}
              className={cn("h-6 w-6", prefixClass)}
            />
          )}
        </div>
        <input
          style={{ paddingRight: computedPaddingRight }}
          type={type}
          className={cn(
            "flex h-10 w-full rounded-2xl border border-input bg-white px-3 py-2 text-sm ring-offset-zinc-900 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground placeholder:text-neutral-400 focus-within:border focus-within:border-secondary-500 focus-within:ring-1 focus-within:ring-secondary-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            prefix && "pl-8",
            prefixText && "pl-10",
            !suffixText && suffix && "pr-8",
            props.readOnly && "cursor-not-allowed text-neutral-400",
            className,
          )}
          {...props}
          ref={ref}
        />
        <div className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3">
          {suffix && (
            <Icon
              name={suffix}
              onClick={onSuffixClick}
              className={cn("h-5 w-5", suffixClass)}
            />
          )}
          {suffixText && (
            <div className="z-10 h-6 bg-white">
              <span className="text-xs">{suffixText}</span>
            </div>
          )}
        </div>
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
